import { useRef, useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import StarterKit from '@tiptap/starter-kit';
import { useEditor, EditorContent } from '@tiptap/react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { HEADER } from 'src/layouts/config-layout';

import { VariableNode } from './variable-extension';
import { ChoiceNode, ChoiceOptionNode, newChoiceId } from './choice-extension';
import { CustomVariableNode } from './custom-variable-extension';
import { SignerFieldNode } from './signer-field-extension';
import { PageBreakNode } from './page-break-extension';
import { ColumnsNode, ColumnNode } from './columns-extension';
import { AGREEMENT_VARIABLES } from './agreement-variables';

// a template saved with an older element shape must not take the whole
// editor down; the owner re-inserts the element instead
function safeContent(value: string) {
  try {
    return value ? JSON.parse(value) : '';
  } catch {
    return '';
  }
}

// ----------------------------------------------------------------------

type Props = {
  value: string;
  onChange: (value: string) => void;
};

export default function AgreementTemplateEditor({ value, onChange }: Props) {
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<HTMLElement | null>(null);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [choiceOptionsText, setChoiceOptionsText] = useState('');
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [customFieldName, setCustomFieldName] = useState('');
  const [customFieldOptional, setCustomFieldOptional] = useState(false);
  const [signerFieldDialogOpen, setSignerFieldDialogOpen] = useState(false);
  const [signerFieldName, setSignerFieldName] = useState('');
  const lastEmittedValue = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        heading: { levels: [1, 2, 3] },
      }),
      VariableNode,
      ChoiceNode,
      ChoiceOptionNode,
      CustomVariableNode,
      SignerFieldNode,
      PageBreakNode,
      ColumnsNode,
      ColumnNode,
    ],
    content: safeContent(value),
    onUpdate: ({ editor: currentEditor }) => {
      const nextValue = currentEditor.isEmpty ? '' : JSON.stringify(currentEditor.getJSON());
      lastEmittedValue.current = nextValue;
      onChange(nextValue);
    },
  });

  // the settings load asynchronously, so adopt outside value changes without
  // clobbering the user's in-progress edits (which we emitted ourselves)
  useEffect(() => {
    if (editor && value !== lastEmittedValue.current) {
      lastEmittedValue.current = value;
      editor.commands.setContent(safeContent(value));
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const toggleButtonSx = { px: 1.25, py: 0.25, typography: 'body2', fontWeight: 700 };

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        '&:focus-within': {
          borderColor: 'primary.main',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          p: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          // stays reachable while scrolling a long template, tucked under the app bar
          position: 'sticky',
          top: { xs: HEADER.vertical.H_MOBILE, lg: HEADER.vertical.H_DESKTOP },
          zIndex: 2,
          bgcolor: 'background.paper',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}
      >
        <ToggleButton
          value="bold"
          size="small"
          sx={toggleButtonSx}
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToggleButton>
        <ToggleButton
          value="italic"
          size="small"
          sx={{ ...toggleButtonSx, fontStyle: 'italic' }}
          selected={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </ToggleButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {([1, 2, 3] as const).map((level) => (
          <ToggleButton
            key={level}
            value={`h${level}`}
            size="small"
            sx={toggleButtonSx}
            selected={editor.isActive('heading', { level })}
            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          >
            H{level}
          </ToggleButton>
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToggleButton
          value="bulletList"
          size="small"
          sx={toggleButtonSx}
          selected={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          size="small"
          sx={toggleButtonSx}
          selected={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToggleButton>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertContent([{ type: 'pageBreak' }, { type: 'paragraph' }])
              .run()
          }
        >
          Page Break
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertContent([
                {
                  type: 'columns',
                  content: [
                    { type: 'column', content: [{ type: 'paragraph' }] },
                    { type: 'column', content: [{ type: 'paragraph' }] },
                  ],
                },
                { type: 'paragraph' },
              ])
              .run()
          }
        >
          Two Columns
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setChoiceOptionsText('');
            setChoiceDialogOpen(true);
          }}
        >
          Insert Choice
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setCustomFieldName('');
            setCustomFieldOptional(false);
            setCustomFieldDialogOpen(true);
          }}
        >
          Insert Custom Field
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setSignerFieldName('');
            setSignerFieldDialogOpen(true);
          }}
        >
          Insert Signer Field
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={(event) => setVariableMenuAnchor(event.currentTarget)}
        >
          Insert Variable
        </Button>
        <Menu
          anchorEl={variableMenuAnchor}
          open={!!variableMenuAnchor}
          onClose={() => setVariableMenuAnchor(null)}
        >
          {AGREEMENT_VARIABLES.map((variable) => (
            <MenuItem
              key={variable.key}
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .insertContent({ type: 'variable', attrs: { key: variable.key } })
                  .run();
                setVariableMenuAnchor(null);
              }}
            >
              {variable.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Box
        onClick={() => editor.chain().focus().run()}
        sx={{
          px: 2,
          py: 1,
          cursor: 'text',
          '& .ProseMirror': {
            minHeight: 200,
            outline: 'none',
            typography: 'body2',
            '& h1': { typography: 'h5', my: 1 },
            '& h2': { typography: 'h6', my: 1 },
            '& h3': { typography: 'subtitle1', my: 1 },
            '& p': { my: 0.5 },
            '& ul, & ol': { pl: 3, my: 0.5 },
          },
          '& .agreement-variable': {
            px: 0.75,
            py: 0.25,
            mx: 0.25,
            borderRadius: 0.75,
            bgcolor: 'action.selected',
            fontSize: '0.8125rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          },
          '& .agreement-signer-field': {
            px: 0.75,
            py: 0.25,
            mx: 0.25,
            borderRadius: 0.75,
            bgcolor: 'info.lighter',
            color: 'info.darker',
            fontSize: '0.8125rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          },
          '& .agreement-choice': {
            px: 1,
            py: 0.5,
            my: 0.5,
            borderRadius: 0.75,
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            color: 'text.primary',
            typography: 'body2',
          },
          '& .agreement-columns': {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            my: 1,
          },
          '& .agreement-column': {
            px: 1,
            py: 0.5,
            minHeight: 40,
            borderRadius: 0.75,
            border: (theme) => `1px dashed ${theme.palette.divider}`,
          },
          '& .agreement-page-break': {
            my: 1.5,
            py: 0.25,
            textAlign: 'center',
            typography: 'caption',
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: 'text.secondary',
            borderTop: (theme) => `2px dashed ${theme.palette.divider}`,
            userSelect: 'none',
            '&.ProseMirror-selectednode': {
              color: 'primary.main',
              borderColor: 'primary.main',
            },
          },
          '& .agreement-choice-option': {
            position: 'relative',
            pl: 3,
            my: 0.25,
            '&::before': {
              content: '"◯"',
              position: 'absolute',
              left: 2,
              color: 'text.secondary',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Dialog open={choiceDialogOpen} onClose={() => setChoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Choice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" pb={1}>
            One option per line. When sending an agreement, you pick which option applies.
            The PDF prints all of them with the selected one checked.
          </Typography>
          <TextField
            label="Options"
            fullWidth
            multiline
            rows={4}
            value={choiceOptionsText}
            onChange={(event) => setChoiceOptionsText(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChoiceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              // one option per line; a single comma-separated line also works
              let lines = choiceOptionsText.split('\n').map((option) => option.trim()).filter(Boolean);
              if (lines.length === 1) {
                lines = lines[0].split(',').map((option) => option.trim()).filter(Boolean);
              }
              if (lines.length < 2) {
                enqueueSnackbar('Please enter at least two options, one per line', { variant: 'error' });
                return;
              }
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'choice',
                  attrs: { id: newChoiceId() },
                  content: lines.map((line) => ({
                    type: 'choiceOption',
                    content: [{ type: 'text', text: line }],
                  })),
                })
                .run();
              setChoiceDialogOpen(false);
            }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={signerFieldDialogOpen}
        onClose={() => setSignerFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Signer Field</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" pb={1}>
            Name the field, for example Social Security Number. The customer fills in its value
            when signing the agreement.
          </Typography>
          <TextField
            label="Field Name"
            fullWidth
            value={signerFieldName}
            onChange={(event) => setSignerFieldName(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignerFieldDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const name = signerFieldName.trim();
              if (!name) {
                enqueueSnackbar('Please enter the field name', { variant: 'error' });
                return;
              }
              editor
                .chain()
                .focus()
                .insertContent({ type: 'signerField', attrs: { name } })
                .run();
              setSignerFieldDialogOpen(false);
            }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={customFieldDialogOpen}
        onClose={() => setCustomFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Custom Field</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" pb={1}>
            Name the field, for example Fuel Surcharge. You fill in its value when sending the
            agreement.
          </Typography>
          <TextField
            label="Field Name"
            fullWidth
            value={customFieldName}
            onChange={(event) => setCustomFieldName(event.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={customFieldOptional}
                onChange={(event) => setCustomFieldOptional(event.target.checked)}
              />
            }
            label="Optional. When left blank, the whole line holding this field is left out of the document"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomFieldDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const name = customFieldName.trim();
              if (!name) {
                enqueueSnackbar('Please enter the field name', { variant: 'error' });
                return;
              }
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'customVariable',
                  attrs: { name, optional: customFieldOptional },
                })
                .run();
              setCustomFieldDialogOpen(false);
            }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
